import { useWorkflows, WorkflowActionTimingFormFields } from "@/pages/workflows/workflow-hook.tsx";
import {
  ApiWorkflowBlockDelayReference,
  ApiWorkflowTriggerScheduleDayType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { Input, Select, Stack, Text, Title } from "@skailer-com/uikit";
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
  UseFormRegister,
  useController,
  useWatch,
} from "react-hook-form";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface WorkflowActionTimingEditorProps<
  TFieldValues extends FieldValues & WorkflowActionTimingFormFields,
> {
  control: Control<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  triggerDateAvailable: boolean;
}

export const WorkflowActionTimingEditor = <
  TFieldValues extends FieldValues & WorkflowActionTimingFormFields,
>({
  control,
  register,
  errors,
  triggerDateAvailable,
}: WorkflowActionTimingEditorProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { getWorkflowTriggerScheduleDayTypeOptions } = useWorkflows();
  const delayReference = useWatch({
    control,
    name: "delay_reference" as FieldPath<TFieldValues>,
  }) as ApiWorkflowBlockDelayReference;
  const delayReferenceController = useController({
    control,
    name: "delay_reference" as FieldPath<TFieldValues>,
  });

  useEffect(() => {
    if (!triggerDateAvailable && delayReference === ApiWorkflowBlockDelayReference.trigger_date) {
      delayReferenceController.field.onChange(ApiWorkflowBlockDelayReference.previous_block);
    }
  }, [delayReference, delayReferenceController.field, triggerDateAvailable]);

  const referenceOptions = [
    {
      id: ApiWorkflowBlockDelayReference.previous_block,
      title: t("workflow:block_delay_reference_previous_block"),
    },
    ...(triggerDateAvailable
      ? [
          {
            id: ApiWorkflowBlockDelayReference.trigger_date,
            title: t("workflow:block_delay_reference_trigger_date"),
          },
        ]
      : []),
  ];

  return (
    <Stack gap="md">
      <Title header={t("common:delay")} size="xs" />
      <Select
        label={t("workflow:block_delay_reference")}
        value={delayReferenceController.field.value as ApiWorkflowBlockDelayReference}
        options={referenceOptions}
        emptyTitle={t("workflow:block_delay_reference")}
        onChange={delayReferenceController.field.onChange}
        error={delayReferenceController.fieldState.error?.message}
      />
      {!triggerDateAvailable && (
        <Text className="text-secondary text-sm">
          {t("workflow:block_delay_trigger_date_unavailable_hint")}
        </Text>
      )}
      <Stack direction="horizontal" gap="md">
        <Input
          type="number"
          min={0}
          label={t("common:days")}
          {...register("delay_days" as FieldPath<TFieldValues>, { valueAsNumber: true })}
          error={errors.delay_days?.message as string | undefined}
        />
        <Input
          type="number"
          min={0}
          label={t("common:hours")}
          {...register("delay_hours" as FieldPath<TFieldValues>, { valueAsNumber: true })}
          error={errors.delay_hours?.message as string | undefined}
        />
        <Input
          type="number"
          min={0}
          label={t("common:minutes")}
          {...register("delay_minutes" as FieldPath<TFieldValues>, { valueAsNumber: true })}
          error={errors.delay_minutes?.message as string | undefined}
        />
      </Stack>
      <Controller
        control={control}
        name={"delay_day_type" as FieldPath<TFieldValues>}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Select
            label={t("workflow:trigger_schedule_day_type")}
            emptyTitle={t("workflow:trigger_schedule_day_type")}
            value={value as ApiWorkflowTriggerScheduleDayType}
            options={getWorkflowTriggerScheduleDayTypeOptions()}
            onChange={onChange}
            error={error?.message}
          />
        )}
      />
      {delayReference === ApiWorkflowBlockDelayReference.trigger_date && (
        <>
          <Text className="text-secondary text-sm">
            {t("workflow:block_delay_trigger_date_timezone_hint")}
          </Text>
        </>
      )}
    </Stack>
  );
};
